import React, { useRef, useState } from "react";
import { Box, TextField, IconButton, InputAdornment, Paper, Typography, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import { IoSend, IoAttach, IoStop, IoAdd, IoImageOutline, IoLibraryOutline, IoCheckmark, IoClose } from "react-icons/io5";
import StagedContent from "./StagedContent";
import VoiceRecordingComponent from "./VoiceRecordingComponent";
import { adminColors, radius, TOUCH_HIT_SX } from "../../theme/adminTheme";

// The composer's "+" menu offers exactly three things, in this order:
// a plain action (attach a file) and two toggles (use class materials,
// create an image). "Search the web" is deliberately not here - it's
// decided server-side with no client-side flag to toggle, so a control for
// it would be decorative. There is also no "Run code" affordance - that was
// never a student-facing option in this composer.
const CAPABILITY_META = {
  useClassMaterials: { label: "Use class materials", pillLabel: "Class materials", icon: IoLibraryOutline },
  createImage: { label: "Create an image", pillLabel: "Create image", icon: IoImageOutline }
};

export default function ChatInput({
  input,
  setInput,
  onSubmit,
  isSearching,
  onStop,
  onFileUpload,
  stagedFiles = [],
  onRemoveStagedFile,
  onPreviewStagedFile,
  showMessage
}) {
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [plusAnchorEl, setPlusAnchorEl] = useState(null);
  // Explicit capability toggles from the "+" menu - forces the matching
  // branch in useChatActions.js (isImageRequest / should_use_documents)
  // instead of that hook's keyword sniffing. Both default off; the hook
  // falls back to keyword detection whenever a flag here is false, so
  // behavior for anyone who never opens this menu is unchanged.
  const [capabilities, setCapabilities] = useState({ useClassMaterials: false, createImage: false });
  const plusMenuOpen = Boolean(plusAnchorEl);

  // Blocks typing/sending while a response is in flight.
  const inputLocked = isSearching;

  const openPlusMenu = (e) => setPlusAnchorEl(e.currentTarget);
  const closePlusMenu = () => setPlusAnchorEl(null);

  const toggleCapability = (key) => {
    setCapabilities((prev) => ({ ...prev, [key]: !prev[key] }));
    closePlusMenu();
  };

  const removeCapability = (key) => {
    setCapabilities((prev) => ({ ...prev, [key]: false }));
  };

  // Capability toggles are a per-message signal (like staged files), so
  // they're cleared once a send actually dispatches - not on every
  // keystroke, and not if the send bailed (e.g. a response already in
  // flight), so a rejected send doesn't silently drop the student's choice.
  const clearCapabilitiesIfDispatched = (dispatched) => {
    if (dispatched) {
      setCapabilities({ useClassMaterials: false, createImage: false });
    }
  };

  const onSendMessage = (e) => {
    e.preventDefault();
    if (input.trim() && !inputLocked) {
      Promise.resolve(onSubmit(e, undefined, capabilities)).then(clearCapabilitiesIfDispatched);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      files.forEach(file => {
        onFileUpload(file, { stage: true });
      });
    }
    e.target.value = '';
  };

  const handleAttachClick = () => {
    closePlusMenu();
    fileInputRef.current?.click();
  };

  const handleVoiceTranscription = (transcriptionResult) => {
    if (transcriptionResult && transcriptionResult.transcript) {
      const newValue = input + (input ? ' ' : '') + transcriptionResult.transcript;
      setInput(newValue);
    }
  };

  // Voice auto-send: onSubmit (sendMessage from useChatActions) takes an
  // explicit text-override parameter, so this calls it synchronously with
  // the transcribed text instead of staging it into `input` via setInput()
  // and deferring the actual send by a tick. If onSubmit bails (e.g. a
  // response is already in flight), fall back to putting the transcript in
  // the composer so the student can still send it manually.
  const handleAutoSendMessage = (transcript) => {
    if (transcript && transcript.trim()) {
      const syntheticEvent = { preventDefault: () => {} };
      onSubmit(syntheticEvent, transcript, capabilities).then((dispatched) => {
        if (!dispatched) {
          setInput(transcript);
        }
        clearCapabilitiesIfDispatched(dispatched);
      });
    }
  };

  const handleInsertText = (text) => {
    if (text) {
      const newValue = input + (input ? ' ' : '') + text;
      setInput(newValue);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      files.forEach(file => {
        onFileUpload(file, { stage: true });
      });
    }
  };

  const handleKeyDown = (e) => {
    // Ignore Enter presses that are part of an IME composition (CJK, etc.)
    // so committing a candidate doesn't accidentally send the message.
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !inputLocked) {
        onSendMessage(e);
      }
    }
    // Shift+Enter falls through to the default TextField behavior, inserting a newline.
  };

  const activeCapabilityKeys = Object.keys(capabilities).filter((key) => capabilities[key]);

  return (
    <Box>
      {/* Staged files display */}
      {stagedFiles && stagedFiles.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <StagedContent
            stagedFiles={stagedFiles}
            onRemoveFile={onRemoveStagedFile}
            onPreviewFile={onPreviewStagedFile}
          />
        </Box>
      )}

      {/* Active capability pills - the composer's only indicator that
          "Use class materials" / "Create an image" is armed for the next
          send. Each is removable independently without opening the "+"
          menu again. */}
      {activeCapabilityKeys.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.25 }}>
          {activeCapabilityKeys.map((key) => {
            const meta = CAPABILITY_META[key];
            return (
              <Box
                key={key}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  pl: 1.25,
                  pr: 0.75,
                  py: 0.5,
                  borderRadius: radius.pill,
                  backgroundColor: adminColors.accentLight,
                  border: `1px solid ${adminColors.accentBorder}`,
                  color: adminColors.accentDark
                }}
              >
                <meta.icon size={13} />
                <Typography variant="caption" sx={{ fontSize: "12.5px", fontWeight: 600, color: "inherit" }}>
                  {meta.pillLabel}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => removeCapability(key)}
                  aria-label={`Turn off ${meta.label}`}
                  sx={{
                    position: "relative",
                    width: 18,
                    height: 18,
                    color: "inherit",
                    "&:hover": { backgroundColor: adminColors.accentBorder },
                    // Same invisible touch-hit expansion as the send/stop
                    // pill below - a visible 44px close button would blow
                    // out this compact pill's shape, so the hit area grows
                    // without the visible glyph growing.
                    "@media (pointer: coarse)": {
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        width: 44,
                        height: 44,
                        transform: "translate(-50%, -50%)"
                      }
                    }
                  }}
                >
                  <IoClose size={12} />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Main input container */}
      <Paper
        elevation={0}
        sx={{
          position: "relative",
          borderRadius: "26px",
          border: "1px solid #e5e7eb",
          backgroundColor: "#fff",
          boxShadow: "0 2px 10px rgba(15, 23, 42, 0.06)",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
          "&:focus-within": {
            borderColor: adminColors.accent,
            boxShadow: "0 2px 14px rgba(79,70,229,0.15)"
          },
          ...(isDragOver && {
            borderColor: adminColors.accent,
            backgroundColor: adminColors.accentLight
          })
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <TextField
          fullWidth
          multiline
          maxRows={8}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Message…"
          disabled={inputLocked}
          sx={{
            "& .MuiOutlinedInput-root": {
              border: "none",
              borderRadius: "24px",
              fontSize: "16px",
              fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              pl: 2,
              pr: 12,
              py: 1.5,
              "& fieldset": { border: "none" },
              "& textarea": {
                "&::placeholder": {
                  color: "#9ca3af",
                  opacity: 1
                }
              }
            },
            "& .MuiOutlinedInput-input": {
              padding: 0
            }
          }}
          // NOTE: `pr: 12` (96px) reserves room for the absolutely
          // positioned mic+send/stop cluster (see endAdornment) - two 32px
          // controls plus a 6px gap, pinned `right: 8`, so the cluster's
          // own left edge sits ~78px from the input's right edge. The
          // extra ~18px beyond that is intentional slack (this pill grows
          // with the "+" button on the left now, and text should never
          // visually crowd the buttons on the right).
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton
                  size="small"
                  onClick={openPlusMenu}
                  aria-label="Add attachment or capability"
                  aria-haspopup="true"
                  aria-expanded={plusMenuOpen}
                  sx={{
                    color: adminColors.inkSoft,
                    border: "1px solid #e5e7eb",
                    "&:hover": {
                      backgroundColor: "rgba(79,70,229,0.08)",
                      color: adminColors.accent,
                      borderColor: adminColors.accentBorder
                    },
                    ...TOUCH_HIT_SX
                  }}
                >
                  <IoAdd size={18} />
                </IconButton>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Box
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75
                  }}
                >
                  <VoiceRecordingComponent
                    onTranscriptionComplete={handleVoiceTranscription}
                    onAutoSendMessage={handleAutoSendMessage}
                    onInsertText={handleInsertText}
                    showMessage={showMessage}
                    compact={true}
                  />

                  {isSearching ? (
                    <IconButton
                      onClick={onStop}
                      aria-label="Stop generating response"
                      sx={{
                        position: "relative",
                        background: adminColors.ink,
                        color: "#fff",
                        width: 32,
                        height: 32,
                        transition: "transform 0.12s ease, box-shadow 0.12s ease",
                        boxShadow: "0 2px 8px rgba(24,24,27,0.35)",
                        "&:hover": {
                          background: "#27272a",
                          transform: "scale(1.06)"
                        },
                        // Expands the tappable area to 44x44 on touch
                        // without growing the visible 32px pill.
                        "@media (pointer: coarse)": {
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            width: 44,
                            height: 44,
                            transform: "translate(-50%, -50%)"
                          }
                        }
                      }}
                    >
                      <IoStop size={16} />
                    </IconButton>
                  ) : (
                    <IconButton
                      onClick={onSendMessage}
                      disabled={!input.trim() || inputLocked}
                      aria-label="Send message"
                      sx={{
                        position: "relative",
                        // Gated on the same "can this actually send right
                        // now" condition as the disabled prop, not just
                        // input.trim(), so the pill doesn't look active
                        // while it's locked during a pending deferred send
                        // (see inputLocked above).
                        background: (input.trim() && !inputLocked)
                          ? `linear-gradient(135deg, ${adminColors.accent} 0%, ${adminColors.accentDark} 100%)`
                          : "#e5e7eb",
                        color: "#fff",
                        width: 32,
                        height: 32,
                        transition: "transform 0.12s ease, box-shadow 0.12s ease",
                        boxShadow: (input.trim() && !inputLocked) ? "0 2px 8px rgba(79,70,229,0.35)" : "none",
                        "&:hover": {
                          background: (input.trim() && !inputLocked)
                            ? adminColors.accentDark
                            : "#e5e7eb",
                          transform: (input.trim() && !inputLocked) ? "scale(1.06)" : "none"
                        },
                        "&:disabled": {
                          color: "#fff"
                        },
                        // Expands the tappable area to 44x44 on touch
                        // without growing the visible 32px pill.
                        "@media (pointer: coarse)": {
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            width: 44,
                            height: 44,
                            transform: "translate(-50%, -50%)"
                          }
                        }
                      }}
                    >
                      <IoSend size={16} />
                    </IconButton>
                  )}
                </Box>
              </InputAdornment>
            )
          }}
        />
      </Paper>

      {/* "+" menu - attach is a plain one-shot action; the other two are
          toggles that stay armed (and visible as pills above) until sent
          or removed. */}
      <Menu
        anchorEl={plusAnchorEl}
        open={plusMenuOpen}
        onClose={closePlusMenu}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: radius.surface,
            minWidth: 220,
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
          }
        }}
      >
        <MenuItem onClick={handleAttachClick}>
          <ListItemIcon>
            <IoAttach size={18} />
          </ListItemIcon>
          <ListItemText>Attach file</ListItemText>
        </MenuItem>
        {Object.entries(CAPABILITY_META).map(([key, meta]) => (
          <MenuItem key={key} onClick={() => toggleCapability(key)}>
            <ListItemIcon>
              <meta.icon size={18} />
            </ListItemIcon>
            <ListItemText>{meta.label}</ListItemText>
            {capabilities[key] && (
              <IoCheckmark size={16} color={adminColors.accent} style={{ marginLeft: 8 }} />
            )}
          </MenuItem>
        ))}
      </Menu>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        multiple
        style={{ display: "none" }}
        accept="image/*,application/pdf,text/*,.doc,.docx,.xls,.xlsx,.ppt,.pptx,application/json,application/zip"
      />

      {/* Drag overlay */}
      {isDragOver && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(79,70,229,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            pointerEvents: "none"
          }}
        >
          <Paper
            sx={{
              p: 4,
              borderRadius: radius.surface,
              border: `2px dashed ${adminColors.accent}`,
              backgroundColor: "#fff"
            }}
          >
            <Box sx={{ textAlign: "center", color: adminColors.accent }}>
              <IoAttach size={48} />
              <Box sx={{ mt: 2, fontWeight: 500 }}>Drop files to upload</Box>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Footnote. At 375px the string measures ~343px at this 12px italic
          size, but the container chain (Chat.js wrapper px:2 + this
          element's own px:2 = 64px) only leaves ~311px — too narrow for one
          line, so below `sm` it wraps instead of ellipsizing (compliance
          copy must stay fully readable). It wraps to exactly 2 lines at
          that width: "LearnAndMasterAI can make mistakes." (~209px) /
          "Check important info." (~128px), both well under 311px. At
          `sm`+ the composer is wide enough for one line, so it reverts to
          the original single-line + ellipsis treatment. */}
      <Typography
        variant="caption"
        sx={{
          display: "block",
          textAlign: "center",
          color: adminColors.inkSoft,
          mt: 2,
          px: 2,
          fontSize: "12px",
          fontStyle: "italic",
          lineHeight: 1.4,
          whiteSpace: { xs: "normal", sm: "nowrap" },
          overflow: { sm: "hidden" },
          textOverflow: { sm: "ellipsis" }
        }}
      >
        LearnAndMasterAI can make mistakes. Check important info.
      </Typography>
    </Box>
  );
}
